// «Обновить» и «Отменить правки» черновика живой статьи. Правило заявки, отказов и отмены —
// общее с курсом (`moderation/lib/draftUpdate.ts`); здесь только ручки статьи и её тексты.
// У статьи нет учеников с прогрессом — шага «ущерб обновления» (`update-impact`) нет.
import { useAppDispatch } from '@/store/hooks'
import {
  articleModerationApi,
  useDiscardArticleDraftMutation,
  useSubmitArticleUpdateMutation,
} from '@/features/moderation/articleModerationApi'
import { useArticleModeration } from '@/features/moderation/useArticleModeration'
import { discardDraft, sendDraftUpdate } from '@/features/moderation/lib/draftUpdate'
import { useGuardedTask } from '@/features/moderation/lib/useGuardedTask'
import type { VisibilityChange } from '@/features/moderation/types'
import { useRetryCountdown } from '@/features/editor/components/publish/useRetryCountdown'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

// 200 — в черновике были только поля без проверки (оформление), они применены сразу.
const doneText = (res: VisibilityChange): string => {
  const m = messagesNow(messages)
  return res.kind === 'review' ? m.updateSentForReview : m.updateApplied
}

/** `id === null` — статья ещё не создана: черновика нет, отправлять нечего. */
export const useArticleUpdate = (id: number | null) => {
  const [submitUpdate] = useSubmitArticleUpdateMutation()
  const [discardArticleDraft] = useDiscardArticleDraftMutation()
  const dispatch = useAppDispatch()
  const { refetch } = useArticleModeration(id)
  const countdown = useRetryCountdown()
  const { guarded, isBusy } = useGuardedTask()

  const submit = () =>
    guarded(async () => {
      if (id === null) return
      await sendDraftUpdate({
        request: () => submitUpdate(id).unwrap(),
        doneText,
        refresh: () =>
          dispatch(
            articleModerationApi.endpoints.getArticleModeration.initiate(id, {
              forceRefetch: true,
              subscribe: false,
            }),
          ),
        refetch,
        startCountdown: countdown.start,
      })
    })

  const discard = () =>
    guarded(async () => {
      if (id === null) return
      await discardDraft(() => discardArticleDraft(id).unwrap(), refetch)
    })

  return { submit, discard, isBusy, retryLabel: countdown.label }
}
